// v1.22.0 · Auditoría interna · gráficos propios en SVG (sin librerías nuevas): medidor, mapa de árbol con desglose,
// cascada, evolución mensual, mapa de calor, diferencias de conciliación y presupuesto. Todos con tooltip al pasar el mouse.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { C, SEV, money, plab, short } from './api';

export function useWidth(min = 280) {
  const ref = useRef(null);
  const [w, setW] = useState(640);
  useEffect(() => {
    if (!ref.current) return undefined;
    const ro = new ResizeObserver((es) => setW(Math.max(min, Math.floor(es[0].contentRect.width))));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, [min]);
  return [ref, w];
}

function useTip() {
  const [tip, setTip] = useState(null);
  const show = (e, html) => {
    const host = e.currentTarget.closest('[data-tipbox]');
    if (!host) return;
    const box = host.getBoundingClientRect();
    setTip({ x: e.clientX - box.left, y: e.clientY - box.top, html });
  };
  const node = tip ? <div className="neu-tip" style={{ left: tip.x, top: tip.y }}>{tip.html}</div> : null;
  return [node, show, () => setTip(null)];
}

const axisTicks = (min, max, n = 4) => {
  const span = max - min || 1;
  const step0 = span / n;
  const mag = 10 ** Math.floor(Math.log10(step0));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= step0) || step0;
  const out = [];
  for (let v = Math.floor(min / step) * step; v <= max + step * 0.001; v += step) out.push(+v.toFixed(6));
  return out;
};

// ───────────────────────── medidor del puntaje de control ─────────────────────────
export function Gauge({ score = 0, label = '' }) {
  const r = 78; const cx = 100; const cy = 96;
  const ang = (v) => Math.PI * (1 + v / 100);
  const pt = (v, rr = r) => [cx + rr * Math.cos(ang(v)), cy + rr * Math.sin(ang(v))];
  const arc = (a, b) => { const [x1, y1] = pt(a); const [x2, y2] = pt(b); return `M${x1},${y1} A${r},${r} 0 0 1 ${x2},${y2}`; };
  const bands = [[0, 40, SEV.alta.color], [40, 60, SEV.media.color], [60, 80, '#eda100'], [80, 100, C.good]];
  const [nx, ny] = pt(Math.max(0, Math.min(100, score)), r - 14);
  return (
    <svg viewBox="0 0 200 122" role="img" aria-label={`Puntaje de control ${score} de 100: ${label}`} style={{ width: '100%', maxWidth: 260 }}>
      <path d={arc(0, 100)} stroke="#d9dee7" strokeWidth="16" fill="none" strokeLinecap="round" />
      {bands.map(([a, b, col]) => <path key={a} d={arc(a + 0.8, b - 0.8)} stroke={col} strokeOpacity={score >= a ? 1 : 0.25} strokeWidth="16" fill="none" />)}
      <line x1={cx} y1={cy} x2={nx} y2={ny} stroke={C.ink} strokeWidth="3" strokeLinecap="round" />
      <circle cx={cx} cy={cy} r="7" fill={C.ink} />
      <text x={cx} y={cy - 26} textAnchor="middle" fontSize="30" fontWeight="800" fill={C.ink}>{Math.round(score)}</text>
      <text x={cx} y={118} textAnchor="middle" fontSize="12" fontWeight="700" fill={C.ink2}>{label}</text>
    </svg>
  );
}

// ───────────────────────── mapa de árbol (estructura) con desglose ─────────────────────────
function squarify(items, x, y, w, h) {
  const total = items.reduce((s, i) => s + i.v, 0);
  if (!items.length || total <= 0) return [];
  const out = [];
  let rest = items.slice().sort((a, b) => b.v - a.v);
  let rx = x; let ry = y; let rw = w; let rh = h;
  while (rest.length) {
    const side = Math.min(rw, rh);
    const sumRest = rest.reduce((s, i) => s + i.v, 0);
    const scale = (rw * rh) / sumRest;
    let row = [rest[0]];
    const worst = (rr) => {
      const s = rr.reduce((a, i) => a + i.v * scale, 0);
      const mx = Math.max(...rr.map((i) => i.v * scale)); const mn = Math.min(...rr.map((i) => i.v * scale));
      return Math.max((side * side * mx) / (s * s), (s * s) / (side * side * mn));
    };
    let i = 1;
    while (i < rest.length && worst([...row, rest[i]]) <= worst(row)) { row = [...row, rest[i]]; i += 1; }
    const rowSum = row.reduce((a, it) => a + it.v * scale, 0);
    const thick = rowSum / side;
    let off = 0;
    for (const it of row) {
      const len = (it.v * scale) / thick;
      if (rw >= rh) out.push({ ...it, x: rx, y: ry + off, w: thick, h: len });
      else out.push({ ...it, x: rx + off, y: ry, w: len, h: thick });
      off += len;
    }
    if (rw >= rh) { rx += thick; rw -= thick; } else { ry += thick; rh -= thick; }
    rest = rest.slice(row.length);
  }
  return out;
}
const CLASS_COLOR = { 1: C.s1, 2: C.s2, 3: C.s3, 4: '#6250d6', 5: '#e87ba4', 6: '#e87ba4' };

export function Treemap({ nodes = [], onPick, height = 340 }) {
  const [ref, w] = useWidth();
  const [tip, show, hide] = useTip();
  const items = nodes.filter((n) => Math.abs(n.amount) >= 0.5).map((n) => ({ n, v: Math.abs(n.amount) }));
  const rects = useMemo(() => squarify(items, 0, 0, w, height), [items, w, height]);   // eslint-disable-line react-hooks/exhaustive-deps
  if (!items.length) return <div ref={ref} className="neu-empty">Sin saldos para mostrar en este nivel.</div>;
  return (
    <div ref={ref} data-tipbox style={{ position: 'relative' }}>
      <svg width={w} height={height} role="img" aria-label="Mapa de árbol de cuentas">
        {rects.map(({ n, x, y, w: rw, h: rh }) => {
          const fl = n.flags || {};
          const sev = fl.alta ? 'alta' : fl.media ? 'media' : fl.baja ? 'baja' : null;
          const base = CLASS_COLOR[n.code[0]] || C.s1;
          const neg = n.amount < 0;
          return (
            <g key={n.code} style={{ cursor: n.children?.length || onPick ? 'pointer' : 'default' }}
               onMouseMove={(e) => show(e, <span><b>{n.code} {n.name}</b><br />{money(n.amount)}{neg ? ' (saldo negativo)' : ''}
                 {sev ? <><br />{SEV[sev].icon} {fl.alta || 0} alta · {fl.media || 0} media · {fl.baja || 0} baja</> : null}
                 {n.children?.length ? <><br />Clic para ver sus {n.children.length} subcuentas</> : null}</span>)}
               onMouseLeave={hide} onClick={() => onPick?.(n)}>
              <rect x={x + 2} y={y + 2} width={Math.max(0, rw - 4)} height={Math.max(0, rh - 4)} rx="10" fill={base}
                    fillOpacity={neg ? 0.35 : 0.88} stroke={sev ? SEV[sev].color : '#e9edf3'} strokeWidth={sev ? 3 : 2}
                    strokeDasharray={neg ? '6 4' : undefined} />
              {rw > 70 && rh > 34 && (
                <>
                  <text x={x + 10} y={y + 20} fontSize="11.5" fontWeight="700" fill={neg ? C.ink : '#fff'}>
                    {(n.name || '').slice(0, Math.max(6, Math.floor(rw / 7.2)))}
                  </text>
                  <text x={x + 10} y={y + 36} fontSize="11" fill={neg ? C.ink2 : 'rgba(255,255,255,.9)'}>{short(n.amount)}</text>
                </>
              )}
              {sev && rw > 26 && rh > 22 && (
                <g transform={`translate(${x + rw - 18},${y + 16})`}>
                  <circle r="8" fill="#fff" /><text textAnchor="middle" y="4" fontSize="10" fontWeight="800" fill={SEV[sev].color}>{SEV[sev].icon}</text>
                </g>
              )}
            </g>
          );
        })}
      </svg>
      {tip}
    </div>
  );
}

// ───────────────────────── cascada del resultado ─────────────────────────
export function Waterfall({ steps = [], resultado = 0, height = 280 }) {
  const [ref, w] = useWidth();
  const [tip, show, hide] = useTip();
  if (!steps.length) return <div ref={ref} className="neu-empty">Sin estado de resultados.</div>;
  const ss = steps.slice(0, 10);
  let run = 0;
  const bars = ss.map((s) => { const b = { ...s, from: run, to: run + s.value }; run += s.value; return b; });
  bars.push({ name: 'Resultado', code: 'R', from: 0, to: resultado, value: resultado, total: true });
  const vals = bars.flatMap((b) => [b.from, b.to]);
  const min = Math.min(0, ...vals); const max = Math.max(0, ...vals);
  const pad = { l: 54, r: 10, t: 16, b: 66 };
  const iw = w - pad.l - pad.r; const ih = height - pad.t - pad.b;
  const y = (v) => pad.t + ih - ((v - min) / (max - min || 1)) * ih;
  const bw = iw / bars.length;
  const ticks = axisTicks(min, max);
  return (
    <div ref={ref} data-tipbox style={{ position: 'relative' }}>
      <svg width={w} height={height} role="img" aria-label="Cascada del resultado">
        {ticks.map((t) => <g key={t}><line x1={pad.l} x2={w - pad.r} y1={y(t)} y2={y(t)} stroke={C.grid} /><text x={pad.l - 6} y={y(t) + 4} textAnchor="end" fontSize="10" fill={C.muted}>{short(t)}</text></g>)}
        <line x1={pad.l} x2={w - pad.r} y1={y(0)} y2={y(0)} stroke={C.muted} />
        {bars.map((b, i) => {
          const top = y(Math.max(b.from, b.to)); const hgt = Math.max(2, Math.abs(y(b.from) - y(b.to)));
          const col = b.total ? (b.value < 0 ? SEV.alta.color : C.s3) : b.value >= 0 ? C.s1 : C.s2;
          const x = pad.l + i * bw + bw * 0.16;
          return (
            <g key={b.code + i} onMouseMove={(e) => show(e, <span><b>{b.name}</b><br />{money(b.value)}</span>)} onMouseLeave={hide}>
              <rect x={x} y={top} width={bw * 0.68} height={hgt} rx="4" fill={col} />
              <text x={x + bw * 0.34} y={top - 4} textAnchor="middle" fontSize="10" fontWeight={b.total ? 800 : 500} fill={C.ink2}>{short(b.value)}</text>
              <text transform={`translate(${x + bw * 0.34},${height - pad.b + 12}) rotate(32)`} fontSize="10" fill={C.ink2}>{(b.name || '').slice(0, 18)}</text>
            </g>
          );
        })}
      </svg>
      <div className="neu-legend"><span><i style={{ background: C.s1 }} />Ingresos</span><span><i style={{ background: C.s2 }} />Gastos y descuentos</span>
        <span><i style={{ background: resultado < 0 ? SEV.alta.color : C.s3 }} />Resultado</span></div>
      {tip}
    </div>
  );
}

// ───────────────────────── barras agrupadas + línea (misma escala, mismo eje) ─────────────────────────
export function MonthBars({ months = [], series, line, height = 260, onPick }) {
  const [ref, w] = useWidth();
  const [tip, show, hide] = useTip();
  if (months.length < 1) return <div ref={ref} className="neu-empty">Cargue el libro mayor para ver la evolución mensual.</div>;
  const vals = months.flatMap((m) => [...series.map((s) => m[s.key] || 0), line ? m[line.key] || 0 : 0]);
  const min = Math.min(0, ...vals); const max = Math.max(0, ...vals);
  const pad = { l: 54, r: 10, t: 14, b: 26 };
  const iw = w - pad.l - pad.r; const ih = height - pad.t - pad.b;
  const y = (v) => pad.t + ih - ((v - min) / (max - min || 1)) * ih;
  const gw = iw / months.length; const bw = Math.min(26, (gw * 0.72) / series.length);
  const ticks = axisTicks(min, max);
  return (
    <div ref={ref} data-tipbox style={{ position: 'relative' }}>
      <svg width={w} height={height} role="img" aria-label="Evolución mensual">
        {ticks.map((t) => <g key={t}><line x1={pad.l} x2={w - pad.r} y1={y(t)} y2={y(t)} stroke={C.grid} /><text x={pad.l - 6} y={y(t) + 4} textAnchor="end" fontSize="10" fill={C.muted}>{short(t)}</text></g>)}
        <line x1={pad.l} x2={w - pad.r} y1={y(0)} y2={y(0)} stroke={C.muted} />
        {months.map((m, i) => {
          const gx = pad.l + i * gw + (gw - bw * series.length) / 2;
          return (
            <g key={m.period} onMouseMove={(e) => show(e, <span><b>{plab(m.period)}</b>{series.map((s) => <React.Fragment key={s.key}><br />{s.label}: {money(m[s.key])}</React.Fragment>)}
              {line ? <><br />{line.label}: {money(m[line.key])}</> : null}</span>)} onMouseLeave={hide} onClick={() => onPick?.(m)} style={{ cursor: onPick ? 'pointer' : 'default' }}>
              <rect x={pad.l + i * gw} y={pad.t} width={gw} height={ih} fill="transparent" />
              {series.map((s, j) => { const v = m[s.key] || 0; return <rect key={s.key} x={gx + j * bw + 1} y={y(Math.max(0, v))} width={bw - 2} height={Math.max(1, Math.abs(y(v) - y(0)))} rx="3" fill={s.color} />; })}
              <text x={pad.l + i * gw + gw / 2} y={height - 8} textAnchor="middle" fontSize="10" fill={C.ink2}>{plab(m.period)}</text>
            </g>
          );
        })}
        {line && <polyline fill="none" stroke={line.color || C.ink} strokeWidth="2" points={months.map((m, i) => `${pad.l + i * gw + gw / 2},${y(m[line.key] || 0)}`).join(' ')} />}
        {line && months.map((m, i) => <circle key={m.period} cx={pad.l + i * gw + gw / 2} cy={y(m[line.key] || 0)} r="4" fill={line.color || C.ink} stroke="#e9edf3" strokeWidth="2" />)}
      </svg>
      <div className="neu-legend">{series.map((s) => <span key={s.key}><i style={{ background: s.color }} />{s.label}</span>)}
        {line && <span><i style={{ background: line.color || C.ink, height: 3, borderRadius: 2 }} />{line.label}</span>}</div>
      {tip}
    </div>
  );
}

// ───────────────────────── líneas de saldos (activo / pasivo / patrimonio) ─────────────────────────
export function Lines({ months = [], series, height = 230 }) {
  const [ref, w] = useWidth();
  const [tip, show, hide] = useTip();
  const [hover, setHover] = useState(null);
  if (months.length < 2) return <div ref={ref} className="neu-empty">Se necesitan al menos dos meses de mayor.</div>;
  const vals = months.flatMap((m) => series.map((s) => m[s.key] || 0));
  const min = Math.min(0, ...vals); const max = Math.max(...vals, 1);
  const pad = { l: 54, r: 26, t: 12, b: 24 };
  const iw = w - pad.l - pad.r; const ih = height - pad.t - pad.b;
  const x = (i) => pad.l + (i * iw) / (months.length - 1);
  const y = (v) => pad.t + ih - ((v - min) / (max - min || 1)) * ih;
  const ticks = axisTicks(min, max);
  return (
    <div ref={ref} data-tipbox style={{ position: 'relative' }}>
      <svg width={w} height={height} role="img" aria-label="Saldos mensuales"
           onMouseMove={(e) => { const r = e.currentTarget.getBoundingClientRect(); const i = Math.round(((e.clientX - r.left - pad.l) / iw) * (months.length - 1)); if (i >= 0 && i < months.length) { setHover(i); show(e, <span><b>{plab(months[i].period)}</b>{series.map((s) => <React.Fragment key={s.key}><br />{s.label}: {money(months[i][s.key])}</React.Fragment>)}</span>); } }}
           onMouseLeave={() => { setHover(null); hide(); }}>
        <g>{ticks.map((t) => <g key={t}><line x1={pad.l} x2={w - pad.r} y1={y(t)} y2={y(t)} stroke={C.grid} /><text x={pad.l - 6} y={y(t) + 4} textAnchor="end" fontSize="10" fill={C.muted}>{short(t)}</text></g>)}</g>
        {hover !== null && <line x1={x(hover)} x2={x(hover)} y1={pad.t} y2={pad.t + ih} stroke={C.muted} strokeDasharray="3 3" />}
        {series.map((s) => <polyline key={s.key} fill="none" stroke={s.color} strokeWidth="2.2" points={months.map((m, i) => `${x(i)},${y(m[s.key] || 0)}`).join(' ')} />)}
        {series.map((s) => { const i = months.length - 1; return <text key={s.key} x={x(i) - 4} y={y(months[i][s.key] || 0) - 6} textAnchor="end" fontSize="10.5" fontWeight="700" fill={C.ink2}>{s.label}</text>; })}
        {months.map((m, i) => <text key={m.period} x={x(i)} y={height - 6} textAnchor="middle" fontSize="10" fill={C.ink2}>{plab(m.period)}</text>)}
      </svg>
      <div className="neu-legend">{series.map((s) => <span key={s.key}><i style={{ background: s.color }} />{s.label}</span>)}</div>
      {tip}
    </div>
  );
}

// ───────────────────────── mapa de calor: área × severidad ─────────────────────────
export function Heatmap({ rows = [], onPick }) {
  const max = Math.max(1, ...rows.flatMap((r) => [r.alta, r.media, r.baja]));
  if (!rows.length) return <div className="neu-empty">Sin banderas abiertas.</div>;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(120px, 1.6fr) repeat(3, minmax(44px, .5fr)) minmax(70px, .8fr)', gap: 6, alignItems: 'center' }}>
      <span className="neu-label">Área</span>
      {['alta', 'media', 'baja'].map((s) => <span key={s} className="neu-label" style={{ textAlign: 'center', color: SEV[s].color }}>{SEV[s].icon} {SEV[s].label}</span>)}
      <span className="neu-label" style={{ textAlign: 'right' }}>Monto</span>
      {rows.slice(0, 14).map((r) => (
        <React.Fragment key={r.area}>
          <button type="button" className="neu-btn neu-btn-sm" style={{ justifyContent: 'flex-start', overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.area} onClick={() => onPick?.(r.area)}>
            {r.area}
          </button>
          {['alta', 'media', 'baja'].map((s) => (
            <div key={s} className="neu-cell" title={`${r[s]} ${SEV[s].label.toLowerCase()}`}
                 style={{ background: r[s] ? SEV[s].color : '#e1e6ee', opacity: r[s] ? 0.35 + (0.65 * r[s]) / max : 1, color: r[s] ? '#fff' : C.muted }}>
              {r[s] || '·'}
            </div>
          ))}
          <span className="tabular" style={{ textAlign: 'right', fontSize: 12, color: C.ink2 }}>{short(r.monto)}</span>
        </React.Fragment>
      ))}
    </div>
  );
}

// ───────────────────────── diferencias de una conciliación ─────────────────────────
export function DiffBars({ rows = [], labelKey = 'period', height = 170 }) {
  const [ref, w] = useWidth();
  const [tip, show, hide] = useTip();
  const rs = rows.filter((r) => r.diferencia !== null && r.diferencia !== undefined);
  if (!rs.length) return <div ref={ref} className="neu-empty">Sin datos comparables.</div>;
  const min = Math.min(0, ...rs.map((r) => r.diferencia)); const max = Math.max(0, ...rs.map((r) => r.diferencia));
  const pad = { l: 50, r: 8, t: 10, b: 24 };
  const iw = w - pad.l - pad.r; const ih = height - pad.t - pad.b;
  const y = (v) => pad.t + ih - ((v - min) / (max - min || 1)) * ih;
  const bw = iw / rs.length;
  return (
    <div ref={ref} data-tipbox style={{ position: 'relative' }}>
      <svg width={w} height={height} role="img" aria-label="Diferencias por período">
        {axisTicks(min, max, 3).map((t) => <g key={t}><line x1={pad.l} x2={w - pad.r} y1={y(t)} y2={y(t)} stroke={C.grid} /><text x={pad.l - 5} y={y(t) + 4} textAnchor="end" fontSize="9.5" fill={C.muted}>{short(t)}</text></g>)}
        <line x1={pad.l} x2={w - pad.r} y1={y(0)} y2={y(0)} stroke={C.muted} />
        {rs.map((r, i) => {
          const bad = Math.abs(r.diferencia) > 1;
          const lab = labelKey === 'period' ? plab(r.period) : String(r[labelKey] || '').split(' ')[0];
          return (
            <g key={i} onMouseMove={(e) => show(e, <span><b>{labelKey === 'period' ? plab(r.period) : r[labelKey]}</b><br />Libros: {money(r.libros)}<br />Soporte: {money(r.soporte)}<br />Diferencia: {money(r.diferencia)}</span>)} onMouseLeave={hide}>
              <rect x={pad.l + i * bw} y={pad.t} width={bw} height={ih} fill="transparent" />
              <rect x={pad.l + i * bw + bw * 0.2} y={y(Math.max(0, r.diferencia))} width={bw * 0.6} height={Math.max(2, Math.abs(y(r.diferencia) - y(0)))} rx="3" fill={bad ? SEV.alta.color : C.good} />
              <text x={pad.l + i * bw + bw / 2} y={height - 8} textAnchor="middle" fontSize="9.5" fill={C.ink2}>{lab}</text>
            </g>
          );
        })}
      </svg>
      {tip}
    </div>
  );
}

export function Spark({ values = [], color = C.s1, width = 110, height = 30 }) {
  if (values.length < 2) return null;
  const min = Math.min(...values); const max = Math.max(...values);
  const pts = values.map((v, i) => `${(i * width) / (values.length - 1)},${height - 3 - ((v - min) / (max - min || 1)) * (height - 6)}`).join(' ');
  return <svg width={width} height={height} aria-hidden="true"><polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" /></svg>;
}
